
import { computed, defineComponent, nextTick, ref, toRef, watch } from "vue";
import { Form, Field, ErrorMessage } from "vee-validate";

import {
  FormatMethods,
  getMaxLengthErrorMessage,
  getTimeZoneList,
} from "@/helpers/data-format";
import AddAddressModal from "./AddAddressModal.vue";

import { SiteApi } from "@/services/api/Site.service";
import * as Yup from "yup";
import { hideModal } from "@/core/helpers/dom";
import { getLookups } from "@/services/api/Lookup.service";
import { useToast } from "vue-toastification";
import { ICloneSite } from "@/interfaces/iSite";
import { useI18n } from "vue-i18n/index";
import { LocationAPI } from "@/services/api/Location.service";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import moment from "moment-timezone";

export default defineComponent({
  name: "user-clone-modal",
  emit: ["clone-submit", "on-cancel"],
  props: {
    site: { type: Object, default: null },
  },
  components: {
    Form,
    Field,
    ErrorMessage,
    AddAddressModal,
    Datepicker,
  },
  setup(props, { emit }) {
    const { t, te } = useI18n();
    function translate(key: string) {
      if (te(key)) {
        return t(key);
      } else {
        return key.split(".").join(" ");
      }
    }
    const toast = useToast();
    const cloneSuccessMsg = translate("pages.site.siteCloneSuccess");
    const newTargetCloneModalRef = ref<null | HTMLElement>(null);
    const addressData = ref<any>("");
    const dialogVisible = ref(true);
    const dialogTitle = computed(
      () => `${translate("common.clone")} - ${siteObj.value?.name || ""}`
    );
    const cloneSiteRef = ref<null | HTMLFormElement>(null);
    const showAddressModal = ref<boolean>(false);
    const isEditMode = ref<boolean>(true);
    const addressVal = ref<any>();
    const loading = ref(false);
    let siteObj = ref<any>({});
    const branchOptions = ref<{ label: string; value: string }[]>([]);
    const cloneData = toRef(props, "site");
    const timeZoneList = computed(() => getTimeZoneList());
    let siteClone;
    const statusOptions = [
      {
        label: t("common.active"),
        value: "Acitve",
      },
      {
        label: t("common.inactive"),
        value: "Inactive",
      },
    ];
    let cloneTargetData = ref<any>({});
    let cloneSiteData = ref({
      validationSchema: Yup.object({
        name: Yup.string()
          .strict(true)
          .max(30, getMaxLengthErrorMessage(t("common.name"), 30))
          .matches(/\S/, t("common.name") + " " + t("common.isRequired"))
          .required(translate("common.name") + " " + t("common.isRequired")),
        startDate: Yup.date()
          .required(
            translate("common.startDate") + " " + t("common.isRequired")
          )
          .nullable(),
        endDate: Yup.date()
          .min(Yup.ref("startDate"), t("validation.greaterEndDate"))
          .nullable(),
        code: Yup.string()
          .strict(true)
          .max(30, getMaxLengthErrorMessage(t("common.number"), 30))
          .required(t("common.number") + " " + t("common.isRequired"))
          .matches(/\S/, t("common.number") + " " + t("common.isRequired")),
        storeEmail: Yup.string()
          .email()
          .matches(
            /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
            t("validation.emailValidation")
          )
          .required(t("common.email") + " " + t("common.isRequired")),
        website: Yup.string()
          .max(100, getMaxLengthErrorMessage(t("pages.site.website"), 100))
          .nullable(),
        address: Yup.string()
          .max(300, getMaxLengthErrorMessage(t("common.address"), 300))
          .required(translate("common.address") + " " + t("common.isRequired")),
        branchType: Yup.string().required(
          t("common.siteLevel") + " " + t("common.isRequired")
        ),
        timezone: Yup.string()
          .required(t("common.timezone") + " " + t("common.isRequired"))
          .nullable(),
        description: Yup.string()
          .max(500, getMaxLengthErrorMessage(t("common.description"), 500))
          .nullable(),
      }),
    });
    siteObj.value = computed(() => {
      return props.site;
    }).value;

    const discard = () => {
      dialogVisible.value = false;
      nextTick(() => {
        cloneSiteRef?.value?.resetForm();
      });
      emit("on-cancel", null);
    };

    const format = computed(() => {
      return FormatMethods;
    });
    const disablePreviousDates = (time) => {
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      return time.getTime() < currentDate.getTime();
    };

    watch(dialogVisible, (val) => {
      if (!val) emit("on-cancel");
    });

    const displayDate = computed(() => {
      return process.env.VUE_APP_DATE_PICKER_FORMAT || "MM-dd-yyyy";
    });
    const operationalHoursEnabled = ref<boolean>(false);
    const daysOfWeek = [
      { label: "Monday", value: "mon" },
      { label: "Tuesday", value: "tue" },
      { label: "Wednesday", value: "wed" },
      { label: "Thursday", value: "thu" },
      { label: "Friday", value: "fri" },
      { label: "Saturday", value: "sat" },
      { label: "Sunday", value: "sun" },
    ];
    const timePickerPopperOptions = computed(() => ({
      strategy: "fixed",
    }));
    const getOperationalHoursOpenError = (dayValue: string) => {
      const op = cloneTargetData.value.operationHours;
      if (!op || !op.week) return null;
      const d = op.week[dayValue];
      if (!d || d.closed) return null;
      if (!d.open) {
        return translate("pages.site.startTimeRequired");
      }
      return null;
    };

    const getOperationalHoursCloseError = (dayValue: string) => {
      const op = cloneTargetData.value.operationHours;
      if (!op || !op.week) return null;
      const d = op.week[dayValue];
      if (!d || d.closed) return null;
      if (!d.close) {
        return translate("pages.site.endTimeRequired");
      }
      return null;
    };

    const onClosedChange = (dayValue: string, fieldName = "operationHours") => {
      const week = cloneTargetData.value[fieldName]?.week;
      if (week && week[dayValue]) {
        if (week[dayValue].closed) {
          delete week[dayValue].open;
          delete week[dayValue].close;
        } else if (!week[dayValue].open || !week[dayValue].close) {
          week[dayValue].open = "00:00";
          week[dayValue].close = "23:59";
        }
      }
    };

    const initializeOperationalHours = () => {
      if (!cloneTargetData.value.operationHours) {
        cloneTargetData.value.operationHours = { week: {} };
      }
      if (!cloneTargetData.value.operationHours.week) {
        cloneTargetData.value.operationHours.week = {};
      }
      daysOfWeek.forEach((day) => {
        if (!cloneTargetData.value.operationHours.week[day.value]) {
          cloneTargetData.value.operationHours.week[day.value] = {
            closed: false,
            open: "00:00",
            close: "23:59",
          };
        } else {
          const dayData = cloneTargetData.value.operationHours.week[day.value];
          dayData.closed = !!dayData.closed;
          if (dayData.closed) {
            delete dayData.open;
            delete dayData.close;
          } else if (!dayData.open || !dayData.close) {
            dayData.open = "00:00";
            dayData.close = "23:59";
          }
        }
      });
      operationalHoursEnabled.value = true;
    };

    const toggleOperationalHours = () => {
      operationalHoursEnabled.value = !operationalHoursEnabled.value;
      if (operationalHoursEnabled.value) {
        initializeOperationalHours();
      } else {
        cloneTargetData.value.operationHours = null;
      }
    };

    const setSiteCloneData = async () => {
      try {
        loading.value = true;
        cloneTargetData.value["status"] = statusOptions[0].value;
        const today = new Date();
        cloneTargetData.value["startDate"] = moment(today).format("YYYY-MM-DD");
        const [formList, userList, locationList] = await Promise.all([
          SiteApi.getInstance().getSiteFormsList(siteObj.value.webId, {
            size: 100000,
            status: "ACTIVE",
          }),
          SiteApi.getInstance().getSiteUserList({
            size: 100000,
            siteId: siteObj.value.webId,
          }),
          LocationAPI.getInstance().siteLocations({
            size: 100000,
            siteId: siteObj.value.webId,
          }),
        ]);
        await getLookups(
          "static-data/filter/LOOKUP_FILTER/filters?filter[lookupName]=LKBranchLevel"
        )
          .then((res) => {
            res.map((data) => {
              branchOptions.value.push({
                label: data.text,
                value: data.value,
              });
            });
          })
          .catch((err) => {
            console.error(err);
          });
        if (formList.content && formList.content.length > 0) {
          cloneSiteData.value["cloneForms"] = formList.content;
        }
        if (userList.content && userList.content.length > 0) {
          cloneSiteData.value["cloneUsers"] = userList.content;
        }
        if (locationList.content && locationList.content.length > 0) {
          cloneSiteData.value["cloneLocations"] = locationList.content;
        }

        cloneTargetData.value["cloneLocations"] = locationList.content.map(
          (obj: any) => {
            return obj.locationId;
          }
        );
        cloneTargetData.value["cloneUsers"] = userList.content.map(
          (obj: any) => {
            return obj.webId;
          }
        );
        cloneTargetData.value["cloneForms"] = formList.content.map(
          (obj: any) => {
            return obj.webId;
          }
        );

        // Initialize operational hours from source site's businessHours if available
        let incomingWeek: any = null;
        if (siteObj.value.businessHours?.week) {
          incomingWeek = { ...siteObj.value.businessHours.week };
        } else {
          incomingWeek = {};
          daysOfWeek.forEach((day) => {
            incomingWeek[day.value] = {
              closed: false,
              open: "00:00",
              close: "23:59",
            };
          });
        }

        cloneTargetData.value.operationHours = {
          week: { ...incomingWeek },
        };

        // Normalize / clean up week data
        daysOfWeek.forEach((day) => {
          let dayData = cloneTargetData.value.operationHours.week[day.value];
          if (!dayData) {
            dayData = {
              closed: false,
              open: "00:00",
              close: "23:59",
            };
            cloneTargetData.value.operationHours.week[day.value] = dayData;
          } else {
            dayData.closed = !!dayData.closed;
            if (dayData.closed) {
              delete dayData.open;
              delete dayData.close;
            } else if (!dayData.open || !dayData.close) {
              dayData.open = "00:00";
              dayData.close = "23:59";
            }
          }
        });
        operationalHoursEnabled.value = true;
      } catch (error) {
        console.log(error);
      } finally {
        loading.value = false;
      }
    };
    const showAddModal = () => {
      addressData.value = null;
      showAddressModal.value = true;
    };

    const updateShowModalVal = (val) => {
      showAddressModal.value = val;
    };

    const submitCloneData = async () => {
      try {
        loading.value = true;

        if (
          operationalHoursEnabled.value &&
          cloneTargetData.value.operationHours?.week
        ) {
          const op = cloneTargetData.value.operationHours;

          const invalidDay = daysOfWeek.find((day) => {
            const d = op.week[day.value];
            if (!d || d.closed) {
              return false;
            }
            return !d.open || !d.close;
          });

          if (invalidDay) {
            loading.value = false;
            return;
          }
        }

        siteClone = {};
        siteClone.name = cloneTargetData.value.name;
        siteClone.branchType = cloneTargetData.value.branchType;
        siteClone.timezone = cloneTargetData.value.timezone;
        siteClone.startDate = cloneTargetData.value.startDate;
        siteClone.endDate = cloneTargetData.value.endDate;
        siteClone.userSiteGlobalModel = {
          userSiteIds:
            cloneTargetData?.value?.["cloneUsers"]?.length > 0
              ? cloneTargetData?.value?.["cloneUsers"]
              : [],
        };
        siteClone.code = cloneTargetData?.value?.["code"];
        siteClone.addressModel = addressVal?.value;
        siteClone.storeEmail = cloneTargetData?.value?.["storeEmail"];
        siteClone.website = cloneTargetData?.value?.["website"];
        siteClone.siteFormGlobalModel = {
          formIds:
            cloneTargetData?.value?.["cloneForms"]?.length > 0
              ? cloneTargetData?.value?.["cloneForms"]
              : [],
        };
        // siteLocationGlobalModel
        siteClone.siteLocationGlobalModel = {
          locationIds:
            cloneTargetData?.value?.["cloneLocations"]?.length > 0
              ? cloneTargetData?.value?.["cloneLocations"]
              : [],
        };

        if (
          operationalHoursEnabled.value &&
          cloneTargetData.value.operationHours
        ) {
          const week = cloneTargetData.value.operationHours.week;
          siteClone.siteBusinessHoursGlobalModel = {
            businessHours: { week },
          };
        }

        const dateFormat = "YYYY-MM-DDTHH:mm:ssZ";
        const startMoment = moment(siteClone.startDate).startOf("day");
        const formattedStartDate = startMoment.format(dateFormat);
        siteClone.startDate = formattedStartDate;

        if (siteClone.endDate) {
          const endMoment = moment(siteClone.endDate).endOf("day");
          const formattedEndDate = endMoment.format(dateFormat);
          siteClone.endDate = formattedEndDate;
        }

        await SiteApi.getInstance().submitCloneSite(siteClone);
        toast.success(cloneSuccessMsg);
        discard();
        emit("clone-submit");
      } catch (error) {
        console.log(error);
      } finally {
        loading.value = false;
      }
    };

    const clearFormData = () => {
      cloneSiteRef?.value?.resetForm();
      cloneTargetData.value = {};
    };

    const addressSubmit = (address) => {
      addressVal.value = address;
      cloneTargetData.value["address"] =
        address["street"] +
        ", " +
        address["street2"] +
        (address["street2"] == "" ? "" : ", ") +
        address["country"] +
        ", " +
        address["state"] +
        ", " +
        address["city"] +
        ", " +
        address["postalCode"];
    };

    const selectAll = (element, array, key?) => {
      if (element.srcElement.checked) {
        cloneTargetData.value[array] = cloneSiteData.value[array]?.map(
          (obj) => {
            return key ? obj[key] : obj.webId;
          }
        );
      } else {
        cloneTargetData.value[array] = [];
      }
    };
    const getClass = (role) => {
      let className = "bg-success";
      if (role == "Manager") {
        className = "bg-info";
      } else if (role == "User") {
        className = "bg-primary";
      } else if (role == "Admin") {
        className = "bg-success";
      }
      return className;
    };

    const isChecked = (array, id) => {
      return (
        cloneTargetData?.value[array]?.filter((objId) => objId == id).length > 0
      );
    };

    const minDate = computed(() => {
      return moment().format("YYYY-MM-DD");
    });
    const minEndDate = computed(() => {
      const today = moment().format("YYYY-MM-DD");
      const startDate = moment(cloneTargetData.value.startDate).format(
        "YYYY-MM-DD"
      );
      if (today >= startDate) {
        return today;
      } else {
        return startDate;
      }
    });

    const updateCloneArray = (element, id, objectId) => {
      if (element.srcElement.checked) {
        if (cloneTargetData?.value[objectId] == undefined) {
          cloneTargetData.value[objectId] = [];
        }
        cloneTargetData?.value[objectId].push(id);
      } else {
        cloneTargetData.value[objectId] = cloneTargetData?.value[
          objectId
        ].filter((objId) => objId != id);
      }
    };
    watch(
      cloneData,
      (newValue) => {
        siteObj.value = { ...newValue };
        clearFormData();
        setSiteCloneData();
        dialogVisible.value = true;
      },
      { immediate: true }
    );
    return {
      dialogVisible,
      dialogTitle,
      timeZoneList,
      translate,
      setSiteCloneData,
      cloneSiteData,
      submitCloneData,
      cloneTargetData,
      getClass,
      updateCloneArray,
      newTargetCloneModalRef,
      format,
      cloneSiteRef,
      discard,
      siteObj,
      isChecked,
      selectAll,
      loading,
      displayDate,
      showAddModal,
      showAddressModal,
      addressData,
      updateShowModalVal,
      isEditMode,
      addressSubmit,
      disablePreviousDates,
      minDate,
      minEndDate,
      statusOptions,
      branchOptions,
      operationalHoursEnabled,
      daysOfWeek,
      toggleOperationalHours,
      getOperationalHoursOpenError,
      getOperationalHoursCloseError,
      onClosedChange,
      timePickerPopperOptions,
    };
  },
});
